import React, { useEffect, useMemo, useRef, useState } from "react";

const CenteredBlackDropdown = ({
  options = [],
  value = null,
  onChange,
  placeholder = "Select…",
  width = "100%",
  borderRadius = 12,
  borderColor = "#6b7280",
  borderWidth = 1,
  backgroundColor = "#0a0a0a",
  textColor = "#b2b7bc",
  separatorColor = "#374151",
  optionHeight = 40,
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const indexByValue = useMemo(() => {
    const map = new Map();
    options.forEach((opt, i) => map.set(opt.value, i));
    return map;
  }, [options]);

  const selectedIndex = value != null ? indexByValue.get(value) ?? -1 : -1;
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const moveActive = (delta) => {
    if (!open) return;
    if (!options.length) return;
    let i = activeIndex;
    const total = options.length;
    for (let step = 0; step < total; step++) {
      i = (i + delta + total) % total;
      if (!options[i].disabled) {
        setActiveIndex(i);
        break;
      }
    }
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        } else {
          moveActive(1);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (open) moveActive(-1);
        break;
      case "Enter":
      case " ":
        if (!open) {
          e.preventDefault();
          setOpen(true);
          setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        } else if (activeIndex >= 0) {
          e.preventDefault();
          const opt = options[activeIndex];
          if (!opt.disabled) {
            if (onChange) { onChange(opt.value, opt); }
            setOpen(false);
          }
        }
        break;
      case "Escape":
        setOpen(false);
        break;
    }
  };

  useEffect(() => {
    if (!open || activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.children[activeIndex];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, open]);

  const styles = {
    root: {
      position: "relative",
      zIndex: open ? 1000 : "auto",
      width,
      fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      color: textColor,
    },
    button: {
      width: "100%",
      height: 44,
      backgroundColor,
      border: `${borderWidth}px solid ${borderColor}`,
      borderRadius,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      cursor: "pointer",
      userSelect: "none",
      padding: "0 36px",
      outline: "none",
      color: textColor,
    },
    caret: {
      position: "absolute",
      right: 10,
      top: "50%",
      transform: "translateY(-50%)",
      pointerEvents: "none",
      display: "inline-flex",
    },
    listbox: {
      position: "absolute",
      top: "100%",
      left: 0,
      width: "100%",
      marginTop: 6,
      backgroundColor,
      border: `${borderWidth}px solid ${borderColor}`,
      borderRadius,
      maxHeight: 300,
      overflowY: "auto",
      boxShadow: "0 6px 16px rgba(0,0,0,0.5)",
      zIndex: 1001,
      padding: 0,
      listStyle: "none",
      color: textColor,
    },
    option: (isActive, isDisabled) => ({
      display: "flex",
      alignItems: "center",
      height: optionHeight,
      padding: "0 5px",
      textAlign: "left",
      borderBottom: `1px solid ${separatorColor}`,
      opacity: isDisabled ? 0.5 : 1,
      background: isActive ? "rgba(255,255,255,0.08)" : "transparent",
      cursor: isDisabled ? "not-allowed" : "pointer",
      color: textColor,
    }),
  };

  return (
    <div ref={rootRef} style={styles.root} onKeyDown={handleKeyDown}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls="dropdown-listbox"
        style={styles.button}
        onClick={() => {
          setOpen(!open);
          setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        }}
      >
        <span style={{ pointerEvents: "none", color: textColor }}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={styles.caret} aria-hidden>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 10l5 5 5-5" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {open && (
        <ul
          id="dropdown-listbox"
          role="listbox"
          ref={listRef}
          style={styles.listbox}
          aria-activedescendant={activeIndex >= 0 ? `opt-${activeIndex}` : undefined}
        >
          {options.map((opt, i) => (
            <li
              id={`opt-${i}`}
              key={String(opt.value)}
              role="option"
              aria-selected={i === selectedIndex}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (!opt.disabled) {
                  if (onChange) { onChange(opt.value, opt); }
                  setOpen(false);
                }
              }}
              style={{
                ...styles.option(i === activeIndex, !!opt.disabled),
                borderBottom: i === options.length - 1 ? "none" : `1px solid ${separatorColor}`,
              }}
            >
              <span style={{ margin: "0 auto" }}>{opt.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CenteredBlackDropdown;
